import { ChevronDownIcon, SparklesIcon } from "lucide-react";
import { useState, type ReactNode } from "react";
import { useReviewSummary, type ReviewKind } from "../../state/reviewSummary";
import { Collapsible, CollapsiblePanel, CollapsibleTrigger } from "../ui/collapsible";
import { Button } from "../ui/button";

export function ReviewSummary({
  organizationId,
  kind,
  entityId,
}: {
  organizationId: string;
  kind: ReviewKind;
  entityId: string;
}) {
  const summary = useReviewSummary(organizationId, kind, entityId);
  return <ReviewSummaryCard {...summary} />;
}

export function ReviewSummaryCard({
  text,
  loading,
  failed,
  retry,
  retryDisabled,
}: {
  text: string | null;
  loading: boolean;
  failed: boolean;
  retry: () => void;
  retryDisabled: boolean;
}) {
  const [open, setOpen] = useState(true);
  return (
    <section aria-label="TL;DR" className="rounded-xl border border-border/60 bg-card shadow-xs">
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger
          aria-label={open ? "Collapse TL;DR" : "Expand TL;DR"}
          className="group flex w-full items-center gap-2.5 rounded-xl px-4 py-3 text-left outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 sm:px-5"
        >
          <span className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-primary/5 text-primary">
            <SparklesIcon aria-hidden className="size-3.5" />
          </span>
          <span className="flex-1 text-sm font-semibold">TL;DR</span>
          <ChevronDownIcon
            aria-hidden
            className={`size-4 text-muted-foreground transition-transform motion-reduce:transition-none ${open ? "rotate-180" : ""}`}
          />
        </CollapsibleTrigger>
        <CollapsiblePanel className="motion-reduce:transition-none">
          <div className="px-4 pb-5 sm:px-5">
            {loading ? (
              <div role="status" aria-label="Writing summary…" className="space-y-2.5 pt-1">
                <span className="sr-only">Writing summary…</span>
                <div aria-hidden className="space-y-2.5 motion-safe:animate-pulse">
                  <div className="h-3 w-full rounded bg-muted" />
                  <div className="h-3 w-11/12 rounded bg-muted" />
                  <div className="h-3 w-2/3 rounded bg-muted" />
                </div>
              </div>
            ) : failed ? (
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p role="status" className="text-sm text-muted-foreground">
                  The summary couldn’t be loaded.
                </p>
                <Button variant="outline" size="sm" onClick={retry} disabled={retryDisabled}>
                  Try again
                </Button>
              </div>
            ) : (
              <p className="whitespace-pre-line break-words text-sm leading-6 text-foreground/85">
                {text}
              </p>
            )}
          </div>
        </CollapsiblePanel>
      </Collapsible>
    </section>
  );
}

export const REVIEW_DETAIL_COLUMNS =
  "grid min-w-0 items-start gap-6 lg:grid-cols-[minmax(0,1fr)_17rem] lg:gap-8";

export function ReviewDetailLayout({
  summary,
  details,
  children,
}: {
  summary: ReactNode;
  details: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className={`mt-6 pb-10 ${REVIEW_DETAIL_COLUMNS}`}>
      <div className="min-w-0 space-y-6">
        {summary}
        {children}
      </div>
      {details}
    </div>
  );
}

export function ReviewDetails({
  kind,
  id,
  children,
}: {
  kind: ReviewKind;
  id: string;
  children: ReactNode;
}) {
  return (
    <aside
      aria-label={kind === "suggestion" ? "Suggestion details" : "Finding details"}
      className="min-w-0 rounded-xl border border-border/60 bg-card shadow-xs lg:sticky lg:top-6"
    >
      <div className="flex items-center justify-between gap-3 px-4 py-3.5">
        <h2 className="text-sm font-semibold">Details</h2>
        <span className="font-mono text-[11px] text-muted-foreground" title={id}>
          #{id.slice(0, 8)}
        </span>
      </div>
      <div className="divide-y divide-border/60">{children}</div>
    </aside>
  );
}

export function ReviewDetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[5rem_minmax(0,1fr)] items-baseline gap-3 py-1.5 text-xs leading-5">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-words text-right font-medium">{children}</dd>
    </div>
  );
}

export function ReviewDetailGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <section className="space-y-2 px-4 py-4">
      <h3 className="text-xs text-muted-foreground">{label}</h3>
      {children}
    </section>
  );
}

/** Neutral assessment surface shared by findings and suggestions. */
export function ReviewAssessmentCard({ children }: { children: ReactNode }) {
  return (
    <section className="space-y-5 rounded-xl border border-border/60 bg-card p-5 text-sm leading-6">
      {children}
    </section>
  );
}
